import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { createDarkTheme, createLightTheme } from '@/theme';
import type { ReactNode } from 'react';

export type ThemeMode = 'dark' | 'light';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'portfolio-theme-mode';

/**
 * Get initial theme mode from localStorage or default to 'dark'
 */
const getInitialThemeMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
  }
  return 'dark'; // Default to dark theme
};

interface ThemeModeProviderProps {
  children: ReactNode;
}

/**
 * ThemeModeProvider - Manages theme state and provides theme switching
 * Persists theme preference to localStorage
 */
export const ThemeModeProvider = ({ children }: ThemeModeProviderProps) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialThemeMode);

  // Persist theme mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => (prevMode === 'dark' ? 'light' : 'dark'));
  }, []);

  const setThemeMode = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
  }, []);

  // Create theme based on current mode - cache themes
  const darkTheme = useMemo(() => createDarkTheme(), []);
  const lightTheme = useMemo(() => createLightTheme(), []);
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  const contextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
      setThemeMode,
    }),
    [mode, toggleTheme, setThemeMode],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

/**
 * Hook to access theme mode context
 */
export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};
